import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { isManagerRole } from "../../lib/labels";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ReasonDialog } from "../../components/dash/ReasonDialog";
import { Section, Pill, ACCOUNT_STATUS, RECIPIENT_LABELS } from "./shared";
import { AccountOtpStep } from "./AccountOtpStep";

const AccountForm = ({ cid, acc, onDone, onCancel }) => {
  const [f, setF] = useState({ bankName: acc.bankName || "", accountNumber: "", accountHolder: acc.accountHolder || "" });
  const [req, setReq] = useState(null);
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put(`/profit/contracts/${cid}/accounts/${acc.recipient}`, f); setReq(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const r = acc.recipient;
  if (req) return <AccountOtpStep req={req} r={r} onDone={onDone} onCancel={() => setReq(null)} />;
  return (
    <form onSubmit={save} className="space-y-2 mt-3" data-testid={`account-form-${r}`}>
      <Input required placeholder="Nama bank" value={f.bankName} onChange={set("bankName")} data-testid={`account-bank-${r}`} />
      <Input required placeholder="Nomor rekening (angka saja)" inputMode="numeric" value={f.accountNumber} onChange={set("accountNumber")} data-testid={`account-number-${r}`} />
      <Input required placeholder="Nama pemilik rekening" value={f.accountHolder} onChange={set("accountHolder")} data-testid={`account-holder-${r}`} />
      <Button className="w-full bg-emerald-800 hover:bg-emerald-900" data-testid={`account-save-${r}`}>Kirim Kode Verifikasi</Button>
      <Button type="button" variant="ghost" className="w-full" onClick={onCancel} data-testid={`account-form-cancel-${r}`}>Batal</Button>
    </form>
  );
};

const fmtAcc = (a) => (a && a.accountNumberMasked ? `${a.bankName} ${a.accountNumberMasked} a.n. ${a.accountHolder}` : "-");
const EVENT_LABELS = { CHANGE_SUBMITTED: "Diajukan (OTP terverifikasi)", PT_SJI_VERIFIED: "Dikonfirmasi PT SJI", PT_SJI_REJECTED: "Ditolak PT SJI" };

const AccountHistory = ({ id, r }) => {
  const [rows, setRows] = useState(null);
  const toggle = async () => {
    if (rows) return setRows(null);
    try { setRows((await api.get(`/profit/accounts/${id}/history`)).data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="mt-3">
      <button type="button" onClick={toggle} className="text-xs font-semibold text-emerald-800" data-testid={`account-history-toggle-${r}`}>{rows ? "Tutup riwayat" : "Lihat riwayat perubahan"}</button>
      {rows && (
        <ul className="mt-2 space-y-2 text-xs text-slate-600" data-testid={`account-history-${r}`}>
          {!rows.length && <li>Belum ada riwayat.</li>}
          {rows.map((h) => (
            <li key={h.id} className="border-l-2 border-emerald-200 pl-2">
              <p className="font-semibold text-slate-800">{EVENT_LABELS[h.event] || h.event} · {new Date(h.at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB</p>
              <p>Oleh: {h.requestedByName || h.reviewedByName || "-"}</p>
              {h.previous && <p>Sebelumnya: {fmtAcc(h.previous)}</p>}
              <p>Baru: {fmtAcc(h.new)}</p>
              {h.note && <p>Catatan: {h.note}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const AccountNumber = ({ acc, admin }) => {
  const [full, setFull] = useState(null);
  const reveal = async () => {
    try { setFull((await api.get(`/profit/accounts/${acc.id}/number`)).data.accountNumber); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <p className="font-mono flex items-center gap-2" data-testid={`account-number-display-${acc.recipient}`}>
      {full || acc.accountNumberMasked}
      {admin && !full && <button type="button" onClick={reveal} className="text-xs font-sans font-semibold text-emerald-800" data-testid={`account-reveal-${acc.recipient}`}>Tampilkan (tercatat di audit log)</button>}
    </p>
  );
};

const AccountCard = ({ acc, cid, reload }) => {
  const { user } = useAuth();
  const [edit, setEdit] = useState(false);
  const [reject, setReject] = useState(false);
  const r = acc.recipient;
  const review = async (decision, note = "") => {
    try { await api.post(`/profit/accounts/${acc.id}/review`, { decision, note }); toast.success("Status rekening diperbarui"); reload(); }
    catch (e) { toast.error(formatApiError(e)); return false; }
  };
  return (
    <div className="rounded-xl border border-slate-200 p-4" data-testid={`account-card-${r}`}>
      <div className="flex items-start justify-between gap-2"><p className="font-bold text-slate-900">{RECIPIENT_LABELS[r]}</p><Pill map={ACCOUNT_STATUS} value={acc.status} testid={`account-status-${r}`} /></div>
      {acc.accountNumberMasked && <div className="mt-2 text-sm text-slate-700"><p>{acc.bankName}</p><AccountNumber acc={acc} admin={user.role === "SUPER_ADMIN"} /><p>a.n. {acc.accountHolder}</p></div>}
      {acc.status === "REJECTED" && acc.reviewNote && <p className="mt-2 text-xs text-red-700">Alasan: {acc.reviewNote}</p>}
      {(r === "INVESTOR" ? user.role === "INVESTOR" : isManagerRole(user.role)) && (edit ? <AccountForm cid={cid} acc={acc} onCancel={() => setEdit(false)} onDone={() => { setEdit(false); reload(); }} />
        : <Button variant="outline" size="sm" className="mt-3" onClick={() => setEdit(true)} data-testid={`account-edit-${r}`}>{acc.accountNumberMasked ? "Ubah Rekening" : "Isi Rekening"}</Button>)}
      {acc.status === "PENDING" && !acc.otpVerifiedAt && <p className="mt-2 text-xs text-amber-800" data-testid={`account-no-otp-${r}`}>Belum diverifikasi kode OTP pemilik rekening. Rekening perlu diajukan ulang.</p>}
      {user.role === "SUPER_ADMIN" && acc.status === "PENDING" && acc.otpVerifiedAt && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" onClick={() => review("VERIFIED")} data-testid={`account-verify-${r}`}>Verifikasi</Button>
          <Button size="sm" variant="outline" onClick={() => setReject(true)} data-testid={`account-reject-${r}`}>Tolak</Button>
        </div>
      )}
      {user.role === "SUPER_ADMIN" && acc.id && <AccountHistory id={acc.id} r={r} />}
      <ReasonDialog open={reject} onOpenChange={setReject} title="Tolak rekening" confirmLabel="Tolak" danger onConfirm={(note) => review("REJECTED", note)} />
    </div>
  );
};

export const AccountsTab = ({ data, reload }) => (
  <Section title="Rekening Tiga Penerima Bagi Hasil" testid="accounts-section">
    <p className="text-sm text-slate-600 mb-4">Investor mengisi rekeningnya sendiri; Pengelola mengisi rekening Pengelola dan Pemilik Lahan. Setiap perubahan wajib dikonfirmasi dengan kode OTP ke email pengaju, lalu dikonfirmasi PT SJI. Transfer PT SJI hanya ke rekening terverifikasi; pembayaran ditahan selama rekening baru diperiksa.</p>
    <div className="grid md:grid-cols-3 gap-4">
      {["INVESTOR", "MANAGER", "LANDOWNER"].map((r) => {
        const acc = data.accounts.find((a) => a.recipient === r);
        return acc && <AccountCard key={r} acc={acc} cid={data.contract.id} reload={reload} />;
      })}
    </div>
  </Section>
);
